import { parseUsd } from "../../shared/billing-money";
import type { BillingInvoice, BillingSubscription } from "../../shared/billing-schemas";
import type { BillingPaymentMethod } from "../../shared/billing-action-schemas";
import {
  cancelBillingSubscription,
  changeInvoiceStatus,
  closeStripeInvoice,
  moveNextInvoice,
  recordInvoicePayment,
  recordPaidOutsideStripe,
  refundBillingInvoice,
  retryStripeInvoice,
  retryStripeSubscription,
} from "./billing-api";
import { formatUsd } from "../../shared/billing-money";
import { dayToSeconds, formatDay } from "./billing-format";
import { BillingFormError, REASON_REQUIREMENT, type FieldGuide } from "./billing-validation";

/**
 * What may be done to one invoice or subscription, and the request each action sends (ADR-0073).
 *
 * A Stripe-backed record can only be changed through Stripe (refund, cancel, retry, or closing an
 * invoice paid some other way, voided, or written off) and needs
 * `billing.collect`; a hand-recorded one moves by hand and needs `billing.manage`. Nothing here
 * touches what a customer can reach.
 */

export type RowAction =
  | { kind: "refund"; invoice: BillingInvoice }
  | { kind: "paid"; invoice: BillingInvoice }
  | { kind: "paid_outside"; invoice: BillingInvoice }
  | { kind: "void"; invoice: BillingInvoice }
  | { kind: "write_off"; invoice: BillingInvoice }
  | { kind: "status"; invoice: BillingInvoice }
  | { kind: "retry_invoice"; invoice: BillingInvoice }
  | { kind: "next_invoice"; subscription: BillingSubscription }
  | { kind: "cancel"; subscription: BillingSubscription }
  | { kind: "retry_subscription"; subscription: BillingSubscription };

export const ROW_ACTION_LABEL: Record<RowAction["kind"], string> = {
  refund: "Refund",
  paid: "Record payment",
  paid_outside: "Paid outside Stripe",
  void: "Void",
  write_off: "Write off",
  next_invoice: "Change next invoice date",
  status: "Change status",
  retry_invoice: "Retry issuing",
  cancel: "Cancel subscription",
  retry_subscription: "Retry creating",
};

export type RowActionDraft = {
  reason: string;
  amount: string;
  day: string;
  status: "past_due" | "void" | "uncollectible";
  /** How money arrived outside Stripe; empty when not said (a hand-recorded invoice only). */
  method: BillingPaymentMethod | "";
  atPeriodEnd: boolean;
};

export const PAYMENT_METHOD_LABEL: Record<BillingPaymentMethod, string> = {
  check: "Check",
  wire: "Wire transfer",
  ach: "ACH transfer",
  other: "Other",
};

const ROW_FIELD_NAMES: Record<string, string> = {
  amountMinor: "amount",
  paidAt: "day",
  nextInvoiceAt: "day",
};

/** What each field of a row action requires, with the amount still refundable named for a refund. */
export function rowActionGuide(action: RowAction): FieldGuide {
  const requirements: Record<string, string> = {
    day:
      action.kind === "next_invoice"
        ? "A day after today and within two years. Nothing is charged or credited for the move."
        : "The day the money arrived, as a date.",
    method: "Check, wire transfer, ACH transfer, or other.",
    status: "Past due, void, or uncollectible.",
    reason: REASON_REQUIREMENT,
  };
  if (action.kind === "refund") {
    const remaining = action.invoice.amountPaidMinor - action.invoice.amountRefundedMinor;
    requirements["amount"] =
      `Dollars and cents, up to ${formatUsd(remaining)}; blank refunds all of it.`;
  }
  return { rename: (field) => ROW_FIELD_NAMES[field] ?? field, requirements };
}

function refundable(invoice: BillingInvoice): boolean {
  return invoice.status === "paid" && invoice.amountPaidMinor > invoice.amountRefundedMinor;
}

function stripeInvoiceActions(invoice: BillingInvoice, canCollect: boolean): RowAction[] {
  if (!canCollect) return [];
  if (invoice.status === "issuing") return [{ kind: "retry_invoice", invoice }];
  if (invoice.status === "open" || invoice.status === "past_due") {
    return [
      { kind: "paid_outside", invoice },
      { kind: "void", invoice },
      { kind: "write_off", invoice },
    ];
  }
  // Money paid by check never passed through Stripe, so Stripe cannot refund it.
  return refundable(invoice) && invoice.paymentMethod === null ? [{ kind: "refund", invoice }] : [];
}

export function invoiceActions(
  invoice: BillingInvoice,
  canManage: boolean,
  canCollect: boolean,
): RowAction[] {
  if (invoice.source === "stripe") return stripeInvoiceActions(invoice, canCollect);
  if (!canManage) return [];
  if (invoice.status === "open" || invoice.status === "past_due") {
    return [
      { kind: "paid", invoice },
      { kind: "status", invoice },
    ];
  }
  return refundable(invoice) ? [{ kind: "refund", invoice }] : [];
}

export function subscriptionActions(
  subscription: BillingSubscription,
  canManage: boolean,
  canCollect: boolean,
): RowAction[] {
  const permitted = subscription.source === "stripe" ? canCollect : canManage;
  if (!permitted || subscription.status === "canceled") return [];
  if (subscription.status === "creating") return [{ kind: "retry_subscription", subscription }];
  const movable =
    subscription.source === "stripe" &&
    subscription.status === "active" &&
    !subscription.cancelAtPeriodEnd;
  return movable
    ? [
        { kind: "next_invoice", subscription },
        { kind: "cancel", subscription },
      ]
    : [{ kind: "cancel", subscription }];
}

function paidDay(draft: RowActionDraft): number {
  const paidAt = dayToSeconds(draft.day);
  if (paidAt === null) throw new BillingFormError({ day: "Enter the date the payment arrived." });
  return paidAt;
}

/** Null refunds whatever remains; anything typed must be an exact, non-zero amount. */
function refundAmount(draft: RowActionDraft): number | null {
  if (draft.amount.trim() === "") return null;
  const amountMinor = parseUsd(draft.amount);
  if (amountMinor === null || amountMinor === 0) {
    throw new BillingFormError({
      amount: "Enter an exact amount, or leave it blank to refund the rest.",
    });
  }
  return amountMinor;
}

function chosenMethod(draft: RowActionDraft): BillingPaymentMethod {
  if (draft.method === "") throw new BillingFormError({ method: "Choose how it was paid." });
  return draft.method;
}

function runInvoiceAction(
  customerSlug: string,
  action: Extract<RowAction, { invoice: BillingInvoice }>,
  draft: RowActionDraft,
): Promise<unknown> {
  const { reason } = draft;
  const id = action.invoice.id;
  switch (action.kind) {
    case "refund":
      return refundBillingInvoice(customerSlug, id, { amountMinor: refundAmount(draft), reason });
    case "paid":
      return recordInvoicePayment(customerSlug, id, {
        paidAt: paidDay(draft),
        method: draft.method === "" ? null : draft.method,
        reason,
      });
    case "paid_outside":
      return recordPaidOutsideStripe(customerSlug, id, {
        paidAt: paidDay(draft),
        method: chosenMethod(draft),
        reason,
      });
    case "void":
    case "write_off":
      return closeStripeInvoice(customerSlug, id, {
        outcome: action.kind === "void" ? "void" : "uncollectible",
        reason,
      });
    case "status":
      return changeInvoiceStatus(customerSlug, id, { status: draft.status, reason });
    default:
      return retryStripeInvoice(customerSlug, id, reason);
  }
}

function runSubscriptionAction(
  customerSlug: string,
  action: Extract<RowAction, { subscription: BillingSubscription }>,
  draft: RowActionDraft,
): Promise<unknown> {
  const { reason } = draft;
  const id = action.subscription.id;
  switch (action.kind) {
    case "next_invoice": {
      const nextInvoiceAt = dayToSeconds(draft.day);
      if (nextInvoiceAt === null) {
        throw new BillingFormError({ day: "Enter the day the next invoice should go out." });
      }
      return moveNextInvoice(customerSlug, id, { nextInvoiceAt, reason });
    }
    case "cancel":
      return cancelBillingSubscription(customerSlug, id, {
        atPeriodEnd: draft.atPeriodEnd,
        reason,
      });
    default:
      return retryStripeSubscription(customerSlug, id, reason);
  }
}

/** Send the request an action describes. Throws a `BillingFormError` naming the field when one is wrong. */
export async function runRowAction(
  customerSlug: string,
  action: RowAction,
  draft: RowActionDraft,
): Promise<unknown> {
  return "invoice" in action
    ? runInvoiceAction(customerSlug, action, draft)
    : runSubscriptionAction(customerSlug, action, draft);
}

/**
 * When a subscription started or starts billing, and what happens next. A subscription created to
 * bill from go-live has a start later than the moment it was created.
 */
export function subscriptionDates(subscription: BillingSubscription): string {
  const scheduled = subscription.startedAt > subscription.createdAt;
  const start = `${scheduled ? "Bills from" : "Started"} ${formatDay(subscription.startedAt)}`;
  if (subscription.canceledAt !== null)
    return `${start} · canceled ${formatDay(subscription.canceledAt)}`;
  // Waiting for go-live, the next invoice is the start itself; saying it twice helps nobody.
  if (
    subscription.currentPeriodEnd === null ||
    (scheduled && subscription.currentPeriodEnd === subscription.startedAt)
  )
    return start;
  const next = formatDay(subscription.currentPeriodEnd);
  return subscription.cancelAtPeriodEnd
    ? `${start} · ends ${next}`
    : `${start} · next invoice ${next}`;
}

type Variant = "default" | "secondary" | "destructive" | "outline";

export function invoiceBadge(status: BillingInvoice["status"]): Variant {
  if (status === "past_due" || status === "uncollectible") return "destructive";
  if (status === "paid") return "default";
  return status === "open" || status === "processing" ? "secondary" : "outline";
}

export function subscriptionBadge(status: BillingSubscription["status"]): Variant {
  if (status === "past_due" || status === "unpaid") return "destructive";
  if (status === "active") return "default";
  return status === "canceled" ? "outline" : "secondary";
}
